import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { findClosestLiveBlock } from './scheduleAvailability';
dayjs.extend(utc);
dayjs.extend(timezone);

function buildBlockKey(h) {
  const startTime = dayjs(h.start).utc().format('HH:mm');
  const endTime = dayjs(h.end).utc().format('HH:mm');
  return `${startTime}|${endTime}`;
}

/**
 * Devuelve los serviceIds efectivos de un bloque, priorizando los del live
 * solo si tiene IDs configurados (evita que [] del live pise los del snapshot).
 */
function pickServiceIds(liveBlock, snapshotBlock) {
  if (liveBlock?.serviceIds?.length) return liveBlock.serviceIds;
  if (snapshotBlock?.serviceIds?.length) return snapshotBlock.serviceIds;
  return [];
}

function snapshotBlockAppliesToDay(snapshotBlock, dayAbbrev) {
  if (!snapshotBlock?.days?.length) return true;
  return snapshotBlock.days.includes(dayAbbrev);
}

function findLiveBlockForSnapshot(snapshotBlock, liveHours, dayAbbrev) {
  const dayHours = (liveHours || []).filter((h) =>
    h?.days?.includes(dayAbbrev),
  );
  if (!dayHours.length) return null;

  const blockKey = buildBlockKey(snapshotBlock);

  // 1) Mismo rango horario (bloque 1: 8-12 no cambió)
  const byTime = dayHours.find((h) => buildBlockKey(h) === blockKey);
  if (byTime) return byTime;

  // 2) Misma sede: bloque live más cercano en horario (bloque 2: 15-22 → live 17-22)
  if (snapshotBlock?.branchId) {
    const sameBranch = dayHours.filter(
      (h) => String(h?.branchId) === String(snapshotBlock.branchId),
    );
    const closestSameBranch = findClosestLiveBlock(snapshotBlock, sameBranch);
    if (closestSameBranch) return closestSameBranch;
  }

  // 3) Sin sedes o sin match: bloque live más cercano del día
  return findClosestLiveBlock(snapshotBlock, dayHours);
}

export function resolveScheduleConfigForPortal({
  dayTurns,
  userScheduleConfig,
  branches,
  date,
  tenantTimezone,
}) {
  const snapshot = dayTurns?.scheduleConfig;
  const everyDeleted = dayTurns?.shifts?.every((s) => s?.softDelete);
  const hasExistingTurns = dayTurns?.shifts?.length > 0 && !everyDeleted;

  const todayStart = dayjs().tz(tenantTimezone).startOf('day');
  const dateStart = dayjs(date).tz(tenantTimezone).startOf('day');
  const isPastDate = dateStart.isBefore(todayStart);

  const hasMultipleBranches =
    Array.isArray(branches) && branches.length >= 2;

  const withLiveServices = (config) => {
    const isPerBlock = userScheduleConfig?.serviceConfigMode === 'perBlock';

    if (!hasMultipleBranches && !isPerBlock) {
      return {
        ...config,
        service: userScheduleConfig?.service ?? config?.service ?? [],
      };
    }

    const dayAbbrev = dayjs(date)
      .tz(tenantTimezone)
      .locale('es')
      .format('dd')
      .toLowerCase();

    const updatedHours = config.hours?.map((snapshotBlock) => {
      if (!snapshotBlockAppliesToDay(snapshotBlock, dayAbbrev)) {
        return snapshotBlock;
      }

      const liveBlock = findLiveBlockForSnapshot(
        snapshotBlock,
        userScheduleConfig?.hours,
        dayAbbrev,
      );

      const enriched = { ...snapshotBlock };

      if (
        hasMultipleBranches &&
        !snapshotBlock?.branchId &&
        liveBlock?.branchId
      ) {
        enriched.branchId = liveBlock.branchId;
      }

      if (isPerBlock) {
        enriched.serviceIds = pickServiceIds(liveBlock, snapshotBlock);
      }

      return enriched;
    });

    if (!isPerBlock) {
      return {
        ...config,
        service: userScheduleConfig?.service ?? config?.service ?? [],
        hours: updatedHours ?? config.hours,
      };
    }

    return {
      ...config,
      serviceConfigMode: 'perBlock',
      service: userScheduleConfig?.service ?? config?.service ?? [],
      hours: updatedHours ?? config.hours,
    };
  };

  // Caso 1: extraDay → respetar horario, sede y servicios configurados al abrir el día.
  if (dayTurns?.extraDay && snapshot) {
    const isPerBlock = userScheduleConfig?.serviceConfigMode === 'perBlock';
    return {
      ...snapshot,
      serviceConfigMode: isPerBlock
        ? 'perBlock'
        : snapshot.serviceConfigMode ?? userScheduleConfig?.serviceConfigMode,
      service: snapshot.service?.length
        ? snapshot.service
        : userScheduleConfig?.service ?? [],
    };
  }

  // Caso 2: fecha pasada con turnos vigentes → snapshot puro
  if (isPastDate && hasExistingTurns && snapshot) {
    return snapshot;
  }

  // Caso 3: hoy/futuro con turnos → preservar horario/sede del snapshot + servicios del live
  if (hasExistingTurns && snapshot) {
    const dayAbbrev = dayjs(date)
      .tz(tenantTimezone)
      .locale('es')
      .format('dd')
      .toLowerCase();
    const dayStillActive = userScheduleConfig?.hours?.some((h) =>
      h?.days?.includes(dayAbbrev),
    );

    if (dayStillActive) {
      return withLiveServices(snapshot);
    }

    return userScheduleConfig;
  }

  // Caso 4: sin turnos → live siempre
  return userScheduleConfig;
}
